import defaultTheme from '../theme';
import { getCustomerSubdomain } from '../organizationType';
import { customerThemes, CustomerTheme, CustomerThemeConfig } from './customerThemes';

export type CustomerThemeId = keyof typeof customerThemes;

// Re-export the interfaces
export type { CustomerTheme, CustomerThemeConfig };

// Export all available themes
export { defaultTheme, customerThemes };

// Function to get theme by customer ID
export function getCustomerTheme(customerId: CustomerThemeId | null): CustomerTheme {
  if (!customerId || !customerThemes[customerId]) {
    return {
      ...defaultTheme,
      customer: {
        id: 'default',
        name: 'Default Theme',
        primaryColor: '#353637',
        secondaryColor: '#81C41D',
      }
    } as CustomerTheme;
  }

  return customerThemes[customerId];
}

// Function to get theme by subdomain
export function getThemeBySubdomain(subdomain: string | null): CustomerTheme {
  if (!subdomain) return getCustomerTheme('default');
  
  // Map subdomains to theme IDs
  const subdomainThemeMap: Record<string, CustomerThemeId> = {
    'recycle': 'recycle',
    'amwaste': 'amwaste',
    'evolution': 'evolution',
    'nerdit': 'nerdit',
    'phoenix': 'phoenix',
    'assettech': 'assettech',
    'drma': 'drma',
    'drm': 'drma',
    'synetic': 'synetic',
    'roctelecom': 'roctelecom',
    'dothavlu': 'dothavlu',
    // Add more mappings as needed
  };

  const themeId = subdomainThemeMap[subdomain.toLowerCase()] || 'default';
  return getCustomerTheme(themeId);
}

// Function to get theme by domain (including root domain)
export function getThemeByDomain(hostname: string): CustomerTheme {
  // Check if it's atportal.co.uk or any subdomain of it
  if (hostname === 'atportal.co.uk' || hostname.endsWith('.atportal.co.uk')) {
    return getCustomerTheme('assettech');
  }

  // Check if it's synetic.tech or any subdomain of it
  if (hostname === 'synetic.tech' || hostname.endsWith('.synetic.tech')) {
    return getCustomerTheme('synetic');
  }

  // Check if it's drmw.tech or any subdomain of it (DRM America)
  if (hostname === 'drmw.tech' || hostname.endsWith('.drmw.tech')) {
    return getCustomerTheme('drma');
  }

  // Customer portals live on *.roctelecom.app (e.g. icbs.roctelecom.app)
  if (hostname === 'roctelecom.app' || hostname.endsWith('.roctelecom.app')) {
    return getCustomerTheme('roctelecom');
  }

  // *.localhost and bare localhost: use same source (query param then sessionStorage then hostname) so impersonation theme is correct
  if (hostname.endsWith('.localhost') || hostname === 'localhost' || hostname === '127.0.0.1') {
    const sub = getCustomerSubdomain();
    return getThemeBySubdomain(sub || 'assettech');
  }

  // Extract subdomain for other domains
  const host = hostname;
  const arr = host.split(".").slice(0, host.includes("localhost") ? -1 : -2);
  if (arr.length > 0) {
    const subdomain = host.includes("vercel.app")
      ? localStorage.getItem("subdomain") || "recycle"
      : arr[0];
    return getThemeBySubdomain(subdomain);
  }

  return getCustomerTheme('default');
}
